import React, { useState } from 'react';
import { Button, Input, Panel, Modal } from '~/shared/Theme';
import { FormControl, HStack, VStack, WarningIcon } from 'native-base';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { Caption } from '~/shared/Typography';
import * as yup from 'yup';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    useGetMember,
    useSpringBigStore,
    useTheme,
    useUpdateMember,
    useUpdatePhoneCheckPin,
    useUpdatePhoneRequest,
} from '@springbig/shared';
import { useToast } from '~/hooks/toast';
import { parseISO, isValid, differenceInYears } from 'date-fns';
import { SpIcon } from '~/shared/Icon';
import PinCodeForm from '~/shared/PinCodeForm/PinCodeForm';

export const ProfileForm = () => {

    // This component is kept exceedingly simple on purpose.

    const {
        primary,
    } = useTheme();

    const { member, walletId, phoneNumber } = useSpringBigStore();

    const {
        refetch: refetchMember,
    } = useGetMember();

    const {
        mutate: updateMember,
        isLoading,
    } = useUpdateMember();

    const { successToast, errorToast } = useToast();

    // PHONE NUMBER ============================================================
    const [isRequestEditingPhoneNumber, setRequestIsEditingPhoneNumber] =
        useState<boolean>(false);
    const [isEditingPhoneNumber, setIsEditingPhoneNumber] =
        useState<boolean>(false);
    const [requestedPhoneNumber, setRequestedPhoneNumber] =
        useState<string>();
    const [isMerger, setIsMerger] =
        useState<boolean>(false);

    const schemaPhoneNumber = yup.object().shape({
        new_phone_number: yup.string().required('Phone number is required').matches(/^\d{10}$/, 'invalid phone number format').trim(),
    });

    const {
        control: controlPhoneNumber,
        handleSubmit: handleSubmitPhoneNumber,
        formState: { errors: errorsPhoneNumber },
    } = useForm({
        resolver: yupResolver(schemaPhoneNumber),
        mode: 'onChange',
    });

    const { field: phoneNumberField } = useController({
        control: controlPhoneNumber,
        defaultValue: member.phone_number,
        name: 'new_phone_number',
    });

    // EMAIL ===================================================================
    const [isEditingEmail, setIsEditingEmail] = useState<boolean>(false);

    const schemaEmail = yup.object().shape({
        email: yup.string().required('Email is required').matches(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,63}$/i, 'invalid email format').trim(),
    });

    const {
        control: controlEmail,
        handleSubmit: handleSubmitEmail,
        formState: { errors: errorsEmail },
    } = useForm({
        resolver: yupResolver(schemaEmail),
        mode: 'onChange',
    });

    const { field: emailField } = useController({
        control: controlEmail,
        defaultValue: member.email,
        name: 'email',
    });

    // FIRST NAME ==============================================================
    const [isEditingFirstName, setIsEditingFirstName] = useState<boolean>(false);

    const schemaFirstName = yup.object().shape({
        first_name: yup
            .string()
            .required('First name is required')
            .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/, 'Invalid characters in first name')
            .min(2)
            .max(50)
            .trim(),
    });

    const {
        control: controlFirstName,
        handleSubmit: handleSubmitFirstName,
        formState: { errors: errorsFirstName },
    } = useForm({
        resolver: yupResolver(schemaFirstName),
        mode: 'onChange',
    });

    const { field: firstNameField } = useController({
        control: controlFirstName,
        defaultValue: member.first_name,
        name: 'first_name',
    });

    // LAST NAME ===============================================================
    const [isEditingLastName, setIsEditingLastName] = useState<boolean>(false);

    const schemaLastName = yup.object().shape({
        last_name: yup
            .string()
            .required('Last name is required')
            .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/, 'Invalid characters in last name')
            .min(2)
            .max(50)
            .trim(),
    });

    const {
        control: controlLastName,
        handleSubmit: handleSubmitLastName,
        formState: { errors: errorsLastName },
    } = useForm({
        resolver: yupResolver(schemaLastName),
        mode: 'onChange',
    });

    const { field: lastNameField } = useController({
        control: controlLastName,
        defaultValue: member.last_name,
        name: 'last_name',
    });

    // BIRTHDAY =================================================================
    const [isEditingBirthday, setIsEditingBirthday] = useState<boolean>(false);

    const schemaBirthday = yup.object().shape({
        birthday: yup
            .string()
            .required('Birthday is required')
            .test('valid-format', 'Invalid date format (YYYY-MM-DD)', (value) => {

                if (!value) {
                    return false;
                }

                const parsed = parseISO(value);
                return isValid(parsed);
            })
            .test('is-21-or-older', 'Must be at least 21 years old', (value) => {

                if (!value) {
                    return false;
                }

                const parsed = parseISO(value);
                if (!isValid(parsed)) {
                    return false;
                }

                return differenceInYears(new Date(), parsed) >= 21;
            })
            .trim(),
    });

    const {
        control: controlBirthday,
        handleSubmit: handleSubmitBirthday,
        formState: { errors: errorsBirthday },
    } = useForm({
        resolver: yupResolver(schemaBirthday),
        mode: 'onChange',
    });

    const { field: birthdayField } = useController({
        control: controlBirthday,
        defaultValue: member.birthday,
        name: 'birthday',
    });

    // ADDRESS 1 ===============================================================
    const [isEditingAddress1, setIsEditingAddress1] = useState<boolean>(false);

    const schemaAddress1 = yup.object().shape({
        address1: yup.string().required('Address is required').max(255).trim(),
    });

    const {
        control: controlAddress1,
        handleSubmit: handleSubmitAddress1,
        formState: { errors: errorsAddress1 },
    } = useForm({
        resolver: yupResolver(schemaAddress1),
        mode: 'onChange',
    });

    const { field: address1Field } = useController({
        control: controlAddress1,
        defaultValue: member.address1,
        name: 'address1',
    });

    // ADDRESS 2 ===============================================================
    const [isEditingAddress2, setIsEditingAddress2] = useState<boolean>(false);

    const schemaAddress2 = yup.object().shape({
        address2: yup.string().max(255).nullable().trim(),
    });

    const {
        control: controlAddress2,
        handleSubmit: handleSubmitAddress2,
        formState: { errors: errorsAddress2 },
    } = useForm({
        resolver: yupResolver(schemaAddress2),
        mode: 'onChange',
    });

    const { field: address2Field } = useController({
        control: controlAddress2,
        defaultValue: member?.address2,
        name: 'address2',
    });

    // CITY ====================================================================
    const [isEditingCity, setIsEditingCity] = useState<boolean>(false);

    const schemaCity = yup.object().shape({
        city: yup.string().required('City is required').max(100).trim(),
    });

    const {
        control: controlCity,
        handleSubmit: handleSubmitCity,
        formState: { errors: errorsCity },
    } = useForm({
        resolver: yupResolver(schemaCity),
        mode: 'onChange',
    });

    const { field: cityField } = useController({
        control: controlCity,
        defaultValue: member.city,
        name: 'city',
    });

    // REGION ==================================================================
    const [isEditingRegion, setIsEditingRegion] = useState<boolean>(false);

    const schemaRegion = yup.object().shape({
        region: yup.string().required('Region is required').max(100).trim(),
    });

    const {
        control: controlRegion,
        handleSubmit: handleSubmitRegion,
        formState: { errors: errorsRegion },
    } = useForm({
        resolver: yupResolver(schemaRegion),
        mode: 'onChange',
    });

    const { field: regionField } = useController({
        control: controlRegion,
        defaultValue: member.region,
        name: 'region',
    });

    // ZIP =====================================================================
    const [isEditingZip, setIsEditingZip] = useState<boolean>(false);

    const schemaZip = yup.object().shape({
        zip: yup
            .string()
            .required('ZIP code is required')
            .matches(/^(\d{5}(?:[-\s]\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$/, 'Invalid ZIP code')
            .max(20)
            .trim(),
    });

    const {
        control: controlZip,
        handleSubmit: handleSubmitZip,
        formState: { errors: errorsZip },
    } = useForm({
        resolver: yupResolver(schemaZip),
        mode: 'onChange',
    });

    const { field: zipField } = useController({
        control: controlZip,
        defaultValue: member.zip,
        name: 'zip',
    });

    // SUBMIT HANDLER ==========================================================

    type Schemas =
        | typeof schemaPhoneNumber
        | typeof schemaEmail
        | typeof schemaFirstName
        | typeof schemaLastName
        | typeof schemaAddress1
        | typeof schemaAddress2
        | typeof schemaCity
        | typeof schemaRegion
        | typeof schemaZip
        | typeof schemaBirthday

    type FormData = yup.InferType<Schemas> & {
        new_phone_number?: string;
    };

    const onSubmit = (data: FormData): void => {

        // Still on purpose. Go l33tcode elsewhere
        setIsEditingPhoneNumber(false);
        setIsEditingEmail(false);
        setIsEditingFirstName(false);
        setIsEditingLastName(false);
        setIsEditingAddress1(false);
        setIsEditingAddress2(false);
        setIsEditingCity(false);
        setIsEditingRegion(false);
        setIsEditingZip(false);

        updateMember({ member: data }, {
            onSuccess: () => {

                successToast('Member updated successfully');
                refetchMember();
            }
        });
    };

    const { mutate: updatePhoneNumberRequest, isLoading: isLoadingUpdatePhoneRequest } = useUpdatePhoneRequest();

    const onSubmitPhoneNumber = (data: FormData): void => {

        if (data.new_phone_number === phoneNumber) {
            return;
        }

        setRequestedPhoneNumber(data.new_phone_number);

        updatePhoneNumberRequest({
            stashboardOrMemberKey: walletId,
            phone_number: data.new_phone_number
        }, {
            onSuccess: (response) => {

                setRequestIsEditingPhoneNumber(true);
                setIsMerger(!!response.is_merger);
            },
            onError: (response: { data?: { error?: string } }) => {

                errorToast(response?.data?.error);
            }
        });
    };

    const { mutate: updatePhoneNumberCheckPin } = useUpdatePhoneCheckPin();

    const onUpdatePhoneCheckPin = (code: string): void => {

        updatePhoneNumberCheckPin({
            stashboardOrMemberKey: walletId,
            pin: btoa(code),
            phone_number: requestedPhoneNumber,
        }, {
            onSuccess: () => {

                setRequestedPhoneNumber(undefined);
                setRequestIsEditingPhoneNumber(false);
                setIsEditingPhoneNumber(false);
                successToast('Phone number successfully updated');
            },
            onError: (response: { data?: { error?: string } }) => {

                setRequestedPhoneNumber(undefined);
                setRequestIsEditingPhoneNumber(false);
                setIsEditingPhoneNumber(false);
                errorToast(response?.data?.error);
            }
        });
    };

    return (
        <Panel space={8}>
            {/* PHONE NUMBER */}
            <Modal isOpen={isRequestEditingPhoneNumber}>
                <Modal.Header>
                    { isMerger ?
                        <Caption textAlign={'center'}>
                            This phone number is already linked to an existing account. To merge accounts, please complete the PIN code verification.
                        </Caption>
                        :
                        <Caption textAlign={'center'}>
                            Please authenticate to update your phone number.
                        </Caption>
                    }
                </Modal.Header>
                <Modal.Body pb={4}>
                    <PinCodeForm
                        onProceed={(code) => {

                            onUpdatePhoneCheckPin(code);
                        }}
                        onCancel={() => setRequestIsEditingPhoneNumber(false)}
                    />
                </Modal.Body>
            </Modal>
            <HStack space={4} alignItems={'flex-end'} w={'full'}>
                <VStack flex={1}>
                    <FormControl isInvalid={!!errorsPhoneNumber.new_phone_number}>
                        <FormControl.Label>
                            <Caption text={'general.phone_number'} />
                            <Caption color={'red'}>*</Caption>
                        </FormControl.Label>
                        <Input
                            {...phoneNumberField}
                            isInvalid={!!errorsPhoneNumber.new_phone_number}
                            isReadOnly={!isEditingPhoneNumber}
                            isDisabled={isLoadingUpdatePhoneRequest}
                            maxLength={10}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage leftIcon={<WarningIcon size={3} />}>
                            {errorsPhoneNumber.new_phone_number?.message}
                        </FormControl.ErrorMessage>
                    </FormControl>
                </VStack>
                {isEditingPhoneNumber ? (
                    <HStack space={4}>
                        <Button
                            text={'general.accept'}
                            disabled={!!errorsPhoneNumber.new_phone_number || phoneNumberField.value === phoneNumber}
                            onPress={handleSubmitPhoneNumber(onSubmitPhoneNumber)}
                            loading={isLoadingUpdatePhoneRequest}
                        />
                        <Button
                            text={'general.cancel'}
                            variant={'neutral'}
                            onPress={() => setIsEditingPhoneNumber(false)}
                            loading={isLoadingUpdatePhoneRequest}
                        />
                    </HStack>
                ) : (
                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        loading={isLoading}
                        onPress={() => setIsEditingPhoneNumber(true)}
                    >
                        <SpIcon
                            color={primary}
                            icon={faEdit}
                        />
                    </Button>
                )}
            </HStack>

            {/* EMAIL */}
            <HStack space={4} alignItems={'flex-end'} w={'full'}>
                <VStack flex={1}>
                    <FormControl isInvalid={!!errorsEmail.email}>
                        <FormControl.Label>
                            <Caption text={'general.email'} />
                            <Caption color={'red'}>*</Caption>
                        </FormControl.Label>
                        <Input
                            {...emailField}
                            isInvalid={!!errorsEmail.email}
                            isReadOnly={!isEditingEmail}
                            isDisabled={isLoading}
                            maxLength={255}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage leftIcon={<WarningIcon size={3} />}>
                            {errorsEmail.email?.message}
                        </FormControl.ErrorMessage>
                    </FormControl>
                </VStack>
                {isEditingEmail ? (
                    <HStack space={4}>
                        <Button text={'general.accept'} disabled={!!errorsEmail.email} onPress={handleSubmitEmail(onSubmit)} />
                        <Button text={'general.cancel'} variant={'neutral'} onPress={() => setIsEditingEmail(false)} />
                    </HStack>
                ) : (
                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        loading={isLoading}
                        onPress={() => setIsEditingEmail(true)}
                    >
                        <SpIcon
                            color={primary}
                            icon={faEdit}
                        />
                    </Button>
                )}
            </HStack>

            {/* FIRST NAME */}
            <HStack space={4} alignItems={'flex-end'} w={'full'}>
                <VStack flex={1}>
                    <FormControl isInvalid={!!errorsFirstName.first_name}>
                        <FormControl.Label>
                            <Caption text={'general.first_name'} />
                            <Caption color={'red'}>*</Caption>
                        </FormControl.Label>
                        <Input
                            {...firstNameField}
                            isInvalid={!!errorsFirstName.first_name}
                            isReadOnly={!isEditingFirstName}
                            isDisabled={isLoading}
                            maxLength={255}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage leftIcon={<WarningIcon size={3} />}>
                            {errorsFirstName.first_name?.message}
                        </FormControl.ErrorMessage>
                    </FormControl>
                </VStack>
                {isEditingFirstName ? (
                    <HStack space={4}>
                        <Button text={'general.accept'} disabled={!!errorsFirstName.first_name} onPress={handleSubmitFirstName(onSubmit)} />
                        <Button text={'general.cancel'} variant={'neutral'} onPress={() => setIsEditingFirstName(false)} />
                    </HStack>
                ) : (
                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        loading={isLoading}
                        onPress={() => setIsEditingFirstName(true)}
                    >
                        <SpIcon
                            color={primary}
                            icon={faEdit}
                        />
                    </Button>
                )}
            </HStack>

            {/* LAST NAME */}
            <HStack space={4} alignItems={'flex-end'} w={'full'}>
                <VStack flex={1}>
                    <FormControl isInvalid={!!errorsLastName.last_name}>
                        <FormControl.Label>
                            <Caption text={'general.last_name'} />
                            <Caption color={'red'}>*</Caption>
                        </FormControl.Label>
                        <Input
                            {...lastNameField}
                            isInvalid={!!errorsLastName.last_name}
                            isReadOnly={!isEditingLastName}
                            isDisabled={isLoading}
                            maxLength={255}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage leftIcon={<WarningIcon size={3} />}>
                            {errorsLastName.last_name?.message}
                        </FormControl.ErrorMessage>
                    </FormControl>
                </VStack>
                {isEditingLastName ? (
                    <HStack space={4}>
                        <Button text={'general.accept'} disabled={!!errorsLastName.last_name} onPress={handleSubmitLastName(onSubmit)} />
                        <Button text={'general.cancel'} variant={'neutral'} onPress={() => setIsEditingLastName(false)} />
                    </HStack>
                ) : (
                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        loading={isLoading}
                        onPress={() => setIsEditingLastName(true)}
                    >
                        <SpIcon
                            color={primary}
                            icon={faEdit}
                        />
                    </Button>
                )}
            </HStack>

            {/* BIRTHDAY */}
            <HStack space={4} alignItems={'flex-end'} w={'full'}>
                <VStack flex={1}>
                    <FormControl isInvalid={!!errorsBirthday.birthday}>
                        <FormControl.Label>
                            <Caption text={'general.birthday'} />
                            <Caption color={'red'}>*</Caption>
                        </FormControl.Label>
                        <Input
                            {...birthdayField}
                            isReadOnly={!isEditingBirthday}
                            isInvalid={!!errorsBirthday.birthday}
                            isDisabled={isLoading}
                            isDate
                            maxLength={10}
                            returnKeyType={'done'}
                            placeholder={'YYYY-MM-DD'}
                        />
                        <FormControl.ErrorMessage leftIcon={<WarningIcon size={3} />}>
                            {errorsBirthday.birthday?.message}
                        </FormControl.ErrorMessage>
                    </FormControl>
                </VStack>
                {isEditingBirthday ? (
                    <HStack space={4}>
                        <Button text={'general.accept'} disabled={!!errorsBirthday.birthday} onPress={handleSubmitBirthday(onSubmit)} />
                        <Button text={'general.cancel'} variant={'neutral'} onPress={() => setIsEditingBirthday(false)} />
                    </HStack>
                ) : (
                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        loading={isLoading}
                        onPress={() => setIsEditingBirthday(true)}
                    >
                        <SpIcon
                            color={primary}
                            icon={faEdit}
                        />
                    </Button>
                )}
            </HStack>

            {/* ADDRESS 1 */}
            <HStack space={4} alignItems={'flex-end'} w={'full'}>
                <VStack flex={1}>
                    <FormControl isInvalid={!!errorsAddress1.address1}>
                        <FormControl.Label>
                            <Caption text={'general.address1'} />
                            <Caption color={'red'}>*</Caption>
                        </FormControl.Label>
                        <Input
                            {...address1Field}
                            isInvalid={!!errorsAddress1.address1}
                            isReadOnly={!isEditingAddress1}
                            isDisabled={isLoading}
                            maxLength={255}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage leftIcon={<WarningIcon size={3} />}>
                            {errorsAddress1.address1?.message}
                        </FormControl.ErrorMessage>
                    </FormControl>
                </VStack>
                {isEditingAddress1 ? (
                    <HStack space={4}>
                        <Button text={'general.accept'} disabled={!!errorsAddress1.address1} onPress={handleSubmitAddress1(onSubmit)} />
                        <Button text={'general.cancel'} variant={'neutral'} onPress={() => setIsEditingAddress1(false)} />
                    </HStack>
                ) : (
                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        loading={isLoading}
                        onPress={() => setIsEditingAddress1(true)}
                    >
                        <SpIcon
                            color={primary}
                            icon={faEdit}
                        />
                    </Button>
                )}
            </HStack>

            {/* ADDRESS 2 */}
            <HStack space={4} alignItems={'flex-end'} w={'full'}>
                <VStack flex={1}>
                    <FormControl isInvalid={!!errorsAddress2.address2}>
                        <FormControl.Label>
                            <Caption text={'general.address2'} />
                        </FormControl.Label>
                        <Input
                            {...address2Field}
                            isInvalid={!!errorsAddress2.address2}
                            isReadOnly={!isEditingAddress2}
                            isDisabled={isLoading}
                            maxLength={255}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage leftIcon={<WarningIcon size={3} />}>
                            {errorsAddress2.address2?.message}
                        </FormControl.ErrorMessage>
                    </FormControl>
                </VStack>
                {isEditingAddress2 ? (
                    <HStack space={4}>
                        <Button text={'general.accept'} disabled={!!errorsAddress2.address2} onPress={handleSubmitAddress2(onSubmit)} />
                        <Button text={'general.cancel'} variant={'neutral'} onPress={() => setIsEditingAddress2(false)} />
                    </HStack>
                ) : (
                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        loading={isLoading}
                        onPress={() => setIsEditingAddress2(true)}
                    >
                        <SpIcon
                            color={primary}
                            icon={faEdit}
                        />
                    </Button>
                )}
            </HStack>

            {/* CITY */}
            <HStack space={4} alignItems={'flex-end'} w={'full'}>
                <VStack flex={1}>
                    <FormControl isInvalid={!!errorsCity.city}>
                        <FormControl.Label>
                            <Caption text={'general.city'} />
                            <Caption color={'red'}>*</Caption>
                        </FormControl.Label>
                        <Input
                            {...cityField}
                            isReadOnly={!isEditingCity}
                            isInvalid={!!errorsCity.city}
                            isDisabled={isLoading}
                            maxLength={100}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage leftIcon={<WarningIcon size={3} />}>
                            {errorsCity.city?.message}
                        </FormControl.ErrorMessage>
                    </FormControl>
                </VStack>
                {isEditingCity ? (
                    <HStack space={4}>
                        <Button text={'general.accept'} disabled={!!errorsCity.city} onPress={handleSubmitCity(onSubmit)} />
                        <Button text={'general.cancel'} variant={'neutral'} onPress={() => setIsEditingCity(false)} />
                    </HStack>
                ) : (
                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        loading={isLoading}
                        onPress={() => setIsEditingCity(true)}
                    >
                        <SpIcon
                            color={primary}
                            icon={faEdit}
                        />
                    </Button>
                )}
            </HStack>

            {/* REGION */}
            <HStack space={4} alignItems={'flex-end'} w={'full'}>
                <VStack flex={1}>
                    <FormControl isInvalid={!!errorsRegion.region}>
                        <FormControl.Label>
                            <Caption text={'general.region'} />
                            <Caption color={'red'}>*</Caption>
                        </FormControl.Label>
                        <Input
                            {...regionField}
                            isReadOnly={!isEditingRegion}
                            isInvalid={!!errorsRegion.region}
                            isDisabled={isLoading}
                            maxLength={100}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage leftIcon={<WarningIcon size={3} />}>
                            {errorsRegion.region?.message}
                        </FormControl.ErrorMessage>
                    </FormControl>
                </VStack>
                {isEditingRegion ? (
                    <HStack space={4}>
                        <Button text={'general.accept'} disabled={!!errorsRegion.region} onPress={handleSubmitRegion(onSubmit)} />
                        <Button text={'general.cancel'} variant={'neutral'} onPress={() => setIsEditingRegion(false)} />
                    </HStack>
                ) : (
                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        loading={isLoading}
                        onPress={() => setIsEditingRegion(true)}
                    >
                        <SpIcon
                            color={primary}
                            icon={faEdit}
                        />
                    </Button>
                )}
            </HStack>

            {/* ZIP */}
            <HStack space={4} alignItems={'flex-end'} w={'full'}>
                <VStack flex={1}>
                    <FormControl isInvalid={!!errorsZip.zip}>
                        <FormControl.Label>
                            <Caption text={'general.zip'} />
                            <Caption color={'red'}>*</Caption>
                        </FormControl.Label>
                        <Input
                            {...zipField}
                            isReadOnly={!isEditingZip}
                            isInvalid={!!errorsZip.zip}
                            isDisabled={isLoading}
                            maxLength={20}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage leftIcon={<WarningIcon size={3} />}>
                            {errorsZip.zip?.message}
                        </FormControl.ErrorMessage>
                    </FormControl>
                </VStack>
                {isEditingZip ? (
                    <HStack space={4}>
                        <Button text={'general.accept'} disabled={!!errorsZip.zip} onPress={handleSubmitZip(onSubmit)} />
                        <Button text={'general.cancel'} variant={'neutral'} onPress={() => setIsEditingZip(false)} />
                    </HStack>
                ) : (
                    <Button
                        modifier={'unlocked'}
                        variant={'text'}
                        loading={isLoading}
                        onPress={() => setIsEditingZip(true)}
                    >
                        <SpIcon
                            color={primary}
                            icon={faEdit}
                        />
                    </Button>
                )}
            </HStack>
        </Panel>
    );
};
